import { UI } from '@skedulo/protobuf-web'
import { compareByRelevance } from '../../utils'
import { UiApiCrudClientBaseV1 } from '../crud-client-base'
import { CacheFactory } from './cache'
import type {
  IApiResource,
  IResourceCreateRequest,
  IResourceDelRequest,
  IResourceGetManyRequest,
  IResourceGetRequest,
  IResourcePutRequest,
} from '../../../types'

const BASE_PATH = '/v1/resources'
const RESOURCES_CACHE_NAME = 'Resources'

const ResourcesCache = CacheFactory.create<
  IResourceGetManyRequest,
  IApiResource[]
>(RESOURCES_CACHE_NAME)

export class ResourceUiApiClientV1 extends UiApiCrudClientBaseV1<IApiResource> {
  create = async (request: IResourceCreateRequest) =>
    this.restPost<IApiResource>(BASE_PATH, request.resource)

  get = async (request: IResourceGetRequest) =>
    this.getItem(
      await this.restGet<IApiResource>(`${BASE_PATH}/${request.uid}`),
    )

  put = async (request: IResourcePutRequest) =>
    this.mergeAndPut(BASE_PATH, request.resource)

  del = async (request: IResourceDelRequest) =>
    this.restDelete(`${BASE_PATH}/${request.uid}`)

  getMany = async (request: IResourceGetManyRequest) =>
    ResourcesCache.execute(request, async () => {
      const { limit, page, name, resource, ...rest } = request as Record<
        string,
        unknown
      >
      const queryParams = this.buildQueryParams({
        limit,
        page,
        name,
        resource,
        ...rest,
      })
      const response = await this.restGet<{ items: IApiResource[] }>(
        BASE_PATH,
        queryParams,
      )
      if (!response.items) throw new Error('Invalid response: missing "items"')
      return response.items.map((item) => this.getItem(item))
    })

  public getItem(crudResource: IApiResource) {
    const { uid, name, resource, datasource } = crudResource
    if (!uid || !name || !resource || typeof datasource === 'undefined') {
      throw Error('Invalid response')
    }
    return {
      ...crudResource,
      uid,
      name,
      resource,
      datasource: UI.CRUD.resource_DatasourceFromJSON(datasource),
    }
  }

  public getByName = async (name: string): Promise<IApiResource | null> => {
    const request = { name, limit: 1000 }
    const result =
      (await this.getMany(request)).sort(compareByRelevance)[0] || null
    if (!result) {
      ResourcesCache.invalidate(request)
    }
    return result
  }
}
